import { memo, useMemo, useState } from "react";
import styles from "./OrderDetailsSideDialog.module.scss";
import CommonSideDialog from "../../../../common/components/side-dialog/CommonSideDialog";
import TabSelector, { Tab } from "../../../../common/components/tab-selector/TabSelector";
import { SortIcon, CloseCircleIcon, ToolsIcon } from "../../../../assets/icons";
import { toast } from "react-toastify";
import { allowedTransitionOrderStates } from "../../../../model/orders/orders";
import SelectOrderStatus, { SelectOrderStatusItem } from "../../../../common/components/select-order-status/SelectOrderStatus";
import { BounceLoader } from "react-spinners";
import { formatDate } from "../../../../utils";
import { OrderStatus, OrderType, ProviderCustomerOrder, SolarOrder } from "../../../../model/customer/customer";
import { BuildingTypeImages } from "../../../../model/building-type";
import { useStartInstallationMutation, useUpdateOrderStatusMutation, useVerifyInstallationMutation } from "../../../../service/order-service";
import { getErrorMessage } from "../../../../utils/error-handler";
import Button from "../../../../common/components/xepp/button/Button";
import Input from "../../../../common/components/new/ui/Input";

type Props = {
	order: SolarOrder;
	onCloseDialog: () => void;
	onUpdateOrderStatus: (orderId: string, newStatus: OrderStatus) => void;
	onRefreshOrders: () => void;
	isReduxQuery?: boolean;
};

const OrderDetailsSideDialog = memo((props: Props) => {
	const { onCloseDialog, order } = props;

	const [selectedTab, setSelectedTab] = useState(1);
	const [status, setStatus] = useState<string>("");
	const [meterNumber, setMeterNumber] = useState<string>("");

	const [updateOrderStatus, { isLoading }] = useUpdateOrderStatusMutation();
	const [startInstallation, { isLoading: isStartingInstallation }] = useStartInstallationMutation();
	const [verifyInstallation, { isLoading: isVerifyingInstallation }] = useVerifyInstallationMutation();

	const orderType = order.details.orderType;

	const tabs: Tab[] = [
		{
			id: 1,
			title: "Overview",
			onClick: () => setSelectedTab(1),
			icon: <SortIcon />,
		},
		{
			id: 2,
			title: "Installation",
			onClick: () => setSelectedTab(2),
			icon: <ToolsIcon height={20} width={20} />,
		},
	];

	const orderStatus = order.details.statusDetails.status;

	const handleCompleteInstallation = async () => {
		// For PayAsYouGo orders, meter number is required
		if (orderType === OrderType.PayAsYouGo && !meterNumber.trim()) {
			toast.error("Meter number is required for Pay As You Go orders");
			return;
		}

		try {
			const payload = orderType === OrderType.PayAsYouGo ? { orderId: order.details.id, meterNumber: meterNumber.trim() } : { orderId: order.details.id };

			await verifyInstallation(payload).unwrap();
			toast.success("Installation completed successfully");
			onCloseDialog();
			setTimeout(() => {
				props.onRefreshOrders();
				// toast.success("Data refreshed successfully");
			}, 5000);
			
		} catch (error: any) {
			toast.error(error?.data?.title || "Failed to complete installation");
		}
	};

	const handleStartInstallation = async () => {
		try {
			await startInstallation(order.details.id).unwrap();
			toast.success("Installation started successfully");
			onCloseDialog();
			setTimeout(() => {
				props.onRefreshOrders();
				// toast.success("Data refreshed successfully");
			}, 5000);
		} catch (error: any) {
			toast.error(error?.data?.title || "Failed to start installation");
		}
	};

	const isPayAsYouGo = orderType === OrderType.PayAsYouGo;
	const showMeterNumberField = orderStatus === OrderStatus.InstallationInProgress && isPayAsYouGo;

	const imageForBuildingType = (buildingType: string) => {
		switch (buildingType) {
			case "Story Building":
				return BuildingTypeImages.storyBuildingImage;
			case "Bungalow":
				return BuildingTypeImages.bungalowImage;
			default:
				return BuildingTypeImages.storyBuildingImage;
		}
	};

	return (
		<CommonSideDialog onCloseDialog={onCloseDialog}>
			<div className={styles.contentWrapper}>
				<div className={styles.header}>
					<div className={styles.closeButton} onClick={onCloseDialog}>
						<CloseCircleIcon />
					</div>
					<h2>Order Details</h2>
					<TabSelector tabs={tabs} selectedTab={selectedTab} />
				</div>

				{selectedTab === 1 && (
					<div>
						<div className={styles.tabContent}>
							{/* <div className={styles.item}>
								<div className={styles.itemTitle}>Status</div>
								<div className={styles.itemSelect}>
									<SelectOrderStatus
										items={statuses}
										placeholder="Select Status"
										onChange={item => {
											console.log(item);
											setStatus(item?.key);
										}}
									/>
								</div>
							</div> */}
							<div className={styles.item}>
								<div className={styles.itemTitle}>Order Id</div>
								<div className={styles.itemValue}>{order.details.id}</div>
							</div>
							<div className={styles.item}>
								<div className={styles.itemTitle}>Customer Name</div>
								<div className={styles.itemValue}>{order.details.contactInformation.fullName}</div>
							</div>
							<div className={styles.item}>
								<div className={styles.itemTitle}>Customer Phone Number</div>
								<div className={styles.itemValue}>{order?.details.contactInformation.phoneNumber}</div>
							</div>
							<div className={styles.item}>
								<div className={styles.itemTitle}>Customer Address</div>
								<div className={styles.itemValue}>{order?.details.deliveryInformation.address}</div>
							</div>
							<div className={styles.item}>
								<div className={styles.itemTitle}>Product Ordered</div>
								<div className={styles.itemValue}>{order.details.productDetails.productName}</div>
							</div>
							<div className={styles.item}>
								<div className={styles.itemTitle}>Created Date</div>
								<div className={styles.itemValue}>{formatDate(order?.details?.createdAt ?? "")}</div>
							</div>
							<div className={styles.item}>
								<div className={styles.itemTitle}>Order Status</div>
								<div className={styles.itemValue}>{OrderStatus[orderStatus]}</div>
							</div>
						</div>

						{/* <div className={styles.btnWrapper}>
							<button className={`standardButton`} disabled={isLoading} onClick={saveNewOrderStatus}>
								{isLoading ? <BounceLoader color="#DE841B" size={25} /> : "Save"}
							</button>
						</div> */}
					</div>
				)}

				{selectedTab === 2 && (
					<div style={{ height: "100%" }}>
						<div className={styles.tabContent} style={{ marginBottom: 20, minHeight: 500}}>
							{/* <div className={styles.imagesContainer}>
								<div className={styles.imageContainer}>
									<img className={styles.image} src={imageForBuildingType(order.details.solarProductId)} alt="Building Type" />
									<div className={styles.imageTitle}>{order?.details?.deliveryInformation?.roofType?.premiseType}</div>
								</div>
								{order.details.deliveryInformation.roofType && (
									<div className={styles.imageContainer}>
										<img className={styles.image} src={order.details.deliveryInformation.roofType.iconUrl} alt="Roof Type" />
										<div className={styles.imageTitle}>{order.details.deliveryInformation.roofType.name}</div>
									</div>
								)}
							</div> */}
							<div className={styles.item}>
								<div className={styles.itemTitle}>Product Ordered</div>
								<div className={styles.itemValue}>{order?.details.productDetails.productName}</div>
							</div>
							<div className={styles.item}>
								<div className={styles.itemTitle}>Installation Address</div>
								<div className={styles.itemValue}>{order?.details.deliveryInformation.address}</div>
							</div>
							{!!order?.details.deliveryInformation.numberOfFloors && (
								<div className={styles.item}>
									<div className={styles.itemTitle}>Number of Floors</div>
									<div className={styles.itemValue}>{order?.details.deliveryInformation.numberOfFloors}</div>
								</div>
							)}
							<div className={styles.item}>
								<div className={styles.itemTitle}>Order Status</div>
								<div className={styles.itemValue}>{OrderStatus[orderStatus]}</div>
							</div>
							{showMeterNumberField && (
								<div className={styles.item}>
									<div className={styles.itemTitle}>Connected Meter Number</div>
									<div className={styles.itemValue}>
										<input placeholder="Enter connected meter number" value={meterNumber} onChange={e => setMeterNumber(e.target.value)} required />
									</div>
								</div>
							)}
							{orderStatus === OrderStatus.DeliveryInProgress && (
								<div className={styles.item}>
									<div className={styles.itemTitle}></div>
									<div>
										<Button 
											text={isStartingInstallation ? "Starting..." : "Start Installation"}
											onClick={handleStartInstallation}
											disabled={isStartingInstallation}
										/>
									</div>
								</div>
							)}
						</div>
						{orderStatus === OrderStatus.InstallationInProgress && (
							<div style={{ textAlign: 'center'}}>
								<Button 
									variant="success" 
									text={isVerifyingInstallation ? "Completing..." : "Complete Installation"}
									onClick={handleCompleteInstallation}
									disabled={isVerifyingInstallation}
								/>
							</div>
						)}
					</div>
				)}
			</div>
		</CommonSideDialog>
	);
});

export default OrderDetailsSideDialog;
